import React from "react";
import { displayedJourney } from "./dataPreparationJourney";
import { LocationDisclosure, OnboardingCollapse, OnboardingCaret } from "./OnboardingDisclosure";
import RightworkSelect from "../../../components/shared/RightworkSelect";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import Tooltip from "@mui/material/Tooltip";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";

import {
  API_confirm_onboarding_setup_locations,
  API_get_onboarding_setup_run,
  API_retry_onboarding_setup,
  API_start_onboarding_setup,
} from "../../../constants";
import {
  getRightworkAPI,
  postRightworkAPI,
} from "../requests/utilities/requests";
import {
  buildDefaultLocationDecisions,
  createClientRequestId,
  extractOnboardingRun,
  getOnboardingErrorMessage,
  isOnboardingRunActive,
  isOnboardingRunTerminal,
  shouldPollOnboardingRun,
} from "./onboardingSetupHelpers";

const POLL_INTERVAL_MS = 4000;
const COMPLETION_REFRESH_RETRY_MS = 1000;
const REVIEW_STATUS = "awaiting_location_review";
const SAVED_PLAN_DISPATCH_ERROR_PATTERN = /plan was saved/i;

function isSavedPlanDispatchError(error) {
  return (
    error?.response?.status === 503 &&
    SAVED_PLAN_DISPATCH_ERROR_PATTERN.test(getOnboardingErrorMessage(error, ""))
  );
}

const PRIMARY_BUTTON_CLASSES = [
  "inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl",
  "bg-violet-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm",
  "transition-colors hover:bg-violet-800 focus:outline-none focus-visible:ring-2",
  "focus-visible:ring-violet-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed",
  "disabled:bg-slate-300 disabled:text-slate-500 disabled:shadow-none",
].join(" ");

const SECONDARY_BUTTON_CLASSES = [
  "inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border",
  "border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700",
  "transition-colors hover:border-violet-300 hover:bg-violet-50 hover:text-violet-800",
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500",
  "focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:border-slate-200",
  "disabled:bg-slate-100 disabled:text-slate-400",
].join(" ");

const ROW_BUTTON_CLASSES = "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600 disabled:cursor-not-allowed disabled:opacity-50";

const STATUS_PRESENTATION = {
  queued: {
    label: "Queued",
    classes: "border-sky-200 bg-sky-50 text-sky-700",
  },
  running: {
    label: "Running",
    classes: "border-violet-200 bg-violet-50 text-violet-700",
  },
  awaiting_location_review: {
    label: "Review needed",
    classes: "border-amber-200 bg-amber-50 text-amber-800",
  },
  failed: {
    label: "Needs attention",
    classes: "border-rose-200 bg-rose-50 text-rose-700",
  },
  completed: {
    label: "Complete",
    classes: "border-emerald-200 bg-emerald-50 text-emerald-700",
  },
};

function ActionSpinner() {
  return <CircularProgress size={17} thickness={5} color="inherit" />;
}

function StatusPill({ status, dispatchStatus }) {
  const dispatchPresentation = dispatchStatus === "dispatch_failed"
    ? { label: "Start failed", classes: "border-rose-200 bg-rose-50 text-rose-700" }
    : dispatchStatus === "dispatch_uncertain"
      ? { label: "Start unconfirmed", classes: "border-amber-200 bg-amber-50 text-amber-800" }
      : null;
  const presentation = dispatchPresentation || STATUS_PRESENTATION[status] || {
    label: (status || "Setup").replace(/_/g, " "),
    classes: "border-slate-200 bg-slate-100 text-slate-700",
  };

  return (
    <span
      className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${presentation.classes}`}
    >
      {presentation.label}
    </span>
  );
}

function formatReasonCode(reasonCode) {
  return String(reasonCode || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function getGroupOptions(run, decisions) {
  const resultGroups = Array.isArray(run?.preflight_results?.groups)
    ? run.preflight_results.groups
    : [];
  const decisionGroups = Array.isArray(run?.preflight_decision?.groups)
    ? run.preflight_decision.groups
    : [];
  const normalized = [...resultGroups, ...decisionGroups]
    .map((group) => ({
      key: group?.key || group?.group_key,
      name: group?.name || group?.key || group?.group_key,
    }))
    .filter((group) => Boolean(group.key));

  if (!normalized.some((group) => group.key === "portfolio_default")) {
    normalized.push({
      key: "portfolio_default",
      name: "Portfolio default",
    });
  }

  decisions.forEach((decision) => {
    if (
      decision.assigned_group_key &&
      !normalized.some((group) => group.key === decision.assigned_group_key)
    ) {
      normalized.push({
        key: decision.assigned_group_key,
        name: decision.assigned_group_key,
      });
    }
  });

  return normalized.filter(
    (group, index, groups) =>
      groups.findIndex((candidate) => candidate.key === group.key) === index
  );
}

function getDecisionValidationMessage(decisions) {
  if (decisions.some((decision) => !decision.assigned_group_key)) {
    return "Choose a productivity group for every location.";
  }
  if (!decisions.some((decision) => !decision.excluded_from_fit)) {
    return "Keep at least one location included in the optimization fit.";
  }

  const groupKeys = [...new Set(decisions.map((decision) => decision.assigned_group_key))];
  const groupWithoutTrainingLocation = groupKeys.find(
    (groupKey) =>
      !decisions.some(
        (decision) =>
          decision.assigned_group_key === groupKey && !decision.excluded_from_fit
      )
  );
  if (groupWithoutTrainingLocation) {
    return "Each productivity group needs at least one included location.";
  }
  return "";
}

function getRetryPresentation(run, dispatchStatus) {
  if (dispatchStatus === "not_dispatched") {
    return {
      heading: "Setup is saved and ready to continue",
      description:
        "Your location decisions are saved, but the next execution has not started. Retry safely continues from that checkpoint.",
      containerClasses: "border-amber-200 bg-amber-50",
      textClasses: "text-amber-900",
      buttonClasses:
        "border-amber-300 text-amber-900 hover:border-amber-400 hover:bg-amber-100",
      Icon: WarningAmberRoundedIcon,
    };
  }
  if (dispatchStatus === "dispatch_uncertain") {
    return {
      heading: "Setup start could not be confirmed",
      description:
        "The plan is saved, but the start response was interrupted. Retry safely reconnects to the same execution without duplicating completed work.",
      containerClasses: "border-amber-200 bg-amber-50",
      textClasses: "text-amber-900",
      buttonClasses:
        "border-amber-300 text-amber-900 hover:border-amber-400 hover:bg-amber-100",
      Icon: WarningAmberRoundedIcon,
    };
  }
  if (dispatchStatus === "dispatch_failed") {
    return {
      heading: "Setup is saved and ready to retry",
      description:
        run?.error_message ||
        "The execution did not start. Retry will use the saved plan and the same idempotent execution identity.",
      containerClasses: "border-rose-200 bg-rose-50",
      textClasses: "text-rose-900",
      buttonClasses:
        "border-rose-300 text-rose-800 hover:border-rose-400 hover:bg-rose-100",
      Icon: ErrorOutlineRoundedIcon,
    };
  }
  return {
    heading: "Setup stopped at a saved checkpoint",
    description:
      run?.error_message ||
      "The last task did not finish. Retry will continue from saved work instead of starting over.",
    containerClasses: "border-rose-200 bg-rose-50",
    textClasses: "text-rose-900",
    buttonClasses:
      "border-rose-300 text-rose-800 hover:border-rose-400 hover:bg-rose-100",
    Icon: ErrorOutlineRoundedIcon,
  };
}

function needsDispatchRetry(run) {
  const dispatchStatus = run?.result_summary?.dispatch_status;
  const rejectedOrUncertain =
    ["queued", "running"].includes(run?.status) &&
    ["dispatch_failed", "dispatch_uncertain"].includes(dispatchStatus);
  const undispatchedContinuation =
    run?.status === "queued" &&
    dispatchStatus === "not_dispatched" &&
    Boolean(run?.preflight_decision_version) &&
    Boolean(run?.preflight_decision);
  return rejectedOrUncertain || undispatchedContinuation;
}

function modelProgress(task) {
  const job = task?.jobStatus?.labor_standard_optimization;
  if (!job) return null;
  const status = task.status === "complete" ? "completed" : task.status === "in_progress" ? "running" : task.status === "in_review" ? "review" : task.status;
  return { status, total: 2, completed: status === "completed" ? 2 : status === "review" || job.status === "completed" || ["publishing", "publish_failed", "review_required"].includes(job.phase) ? 1 : 0, phase: job.phase, message: job.message };
}

function ManualModelProgress({ task, onOpen }) {
  const progress = modelProgress(task);
  const label = progress.status === "completed" ? "Complete" : progress.phase === "publish_failed" ? "Publishing failed" : progress.phase === "publishing" ? "Publishing" : progress.status === "running" ? "Building" : "Review model";
  return <section className="border-t border-slate-100 py-4">
    <div className="flex min-h-[64px] items-center gap-3">
      {progress.status === "completed" ? <CheckCircleRoundedIcon className="text-emerald-500" fontSize="small" /> : <RadioButtonUncheckedIcon className="text-slate-300" fontSize="small" />}
      <div className="min-w-0 flex-1"><h3 className="text-sm font-medium text-slate-900">Labor model <span className="ml-2 text-xs font-normal text-slate-500">{label}</span></h3>
        <div role="progressbar" aria-label="Labor model progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress.completed / 2 * 100} className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100"><div className="h-full bg-violet-700" style={{ width: `${progress.completed / 2 * 100}%` }} /></div>
        {progress.message && <p className="mt-2 text-xs text-rose-700">{progress.message}</p>}
      </div>
      {onOpen && <button type="button" className={ROW_BUTTON_CLASSES} onClick={onOpen}>{progress.phase === "publish_failed" ? "Retry publishing" : progress.status === "completed" ? "View labor model" : progress.status === "review" ? "Review model" : "View progress"}</button>}
    </div>
  </section>;
}

function CardHeader({ stores, run, open, onToggle, contentId, internalOnly, additionalTasks, manualModelTask }) {
  const manual = modelProgress(manualModelTask);
  const steps = displayedJourney(run, stores).map(step => step.id === "model" && manual ? { ...step, ...manual } : step);
  const additionalComplete = additionalTasks.filter((task) => ["complete", "completed", "not_applicable"].includes(task.status)).length;
  const total = steps.length + additionalTasks.length;
  const completed = steps.filter((step) => step.status === "completed").length + additionalComplete;
  const percent = Math.round((steps.reduce((total, step) => total + step.completed / step.total, 0) + additionalComplete) / total * 100);
  return <button type="button" aria-expanded={open} aria-controls={contentId} onClick={onToggle}
    className="flex w-full items-center gap-3 rounded-2xl px-5 py-4 text-left transition-colors duration-300 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-600">
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-baseline gap-2">
        <h2 id="recommended-onboarding-title" className="text-base font-medium text-slate-900">Data preparation</h2>
        <span className="text-xs text-slate-500">{completed} of {total} complete</span>
        {internalOnly && <span className="text-xs text-slate-400">· internal only</span>}
        {run && <StatusPill status={run.status} dispatchStatus={run?.result_summary?.dispatch_status} />}
      </div>
      <div role="progressbar" aria-label="Data preparation progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}
        className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full bg-violet-700 transition-all motion-reduce:transition-none" style={{ width: `${percent}%` }} />
      </div>
    </div>
    <OnboardingCaret open={open} />
  </button>;
}

function PreparationPhase({ step, run, stores, onStart, disabled, blockedReason, revealToken, requestedPhase }) {
  const [open, setOpen] = React.useState(false);
  const contentId = React.useId();
  React.useEffect(() => { if (revealToken && requestedPhase === step.id) setOpen(true); }, [revealToken, requestedPhase, step.id]);
  const labels = { completed: "Complete", failed: "Failed", running: "Running", review: "Review needed", waiting: run ? "Waiting" : "Not started", queued: "Queued", not_started: "Not started" };
  const index = ["history", "model", "outputs"].indexOf(step.id);
  const rows = stores.filter((store) => !run || (run.store_ids || []).map(String).includes(String(store.store_id)))
    .map((store) => ({ ...store, progress: displayedJourney(run, stores, store.store_id)[index] }));
  const completed = rows.filter((row) => row.progress.status === "completed").length;
  const percent = step.total ? Math.round(step.completed / step.total * 100) : 0;
  const canRequest = step.status === "not_started";
  return <section className="border-t border-slate-100">
    <div className="relative -mx-2 rounded-lg px-2 transition-colors duration-300 hover:bg-slate-50">
      <button type="button" aria-expanded={open} aria-controls={contentId} onClick={() => setOpen(!open)} className="flex min-h-[64px] w-full items-center gap-3 py-3 pr-1 text-left focus-visible:outline-violet-600">
        {step.status === "completed" ? <CheckCircleRoundedIcon className="text-emerald-500" fontSize="small" /> : <RadioButtonUncheckedIcon className="text-slate-300" fontSize="small" />}
        <div className="min-w-0 flex-1 pr-28"><div className="flex flex-wrap items-baseline gap-2"><h3 className="text-sm font-medium text-slate-900">{step.label}</h3><span className="text-xs text-slate-500">{completed} of {rows.length} locations complete</span></div>
          {step.status !== "not_started" && <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100"><div className="h-full bg-violet-700 transition-all" style={{ width: `${percent}%` }} /></div>}
        </div><OnboardingCaret open={open} />
      </button>
      {canRequest ? <Tooltip title={blockedReason || ""} describeChild><span className="absolute right-14 top-1/2 -translate-y-1/2"><button type="button" disabled={disabled} title={blockedReason || undefined} aria-label={`${run && isOnboardingRunActive(run) ? "Queue" : "Start"} ${step.label.toLowerCase()}`} onClick={() => onStart(step.id)} className={ROW_BUTTON_CLASSES}>{run && isOnboardingRunActive(run) ? "Queue" : "Start"}</button></span></Tooltip>
        : <span className="pointer-events-none absolute right-14 top-1/2 -translate-y-1/2 text-xs text-slate-500">{labels[step.status]}</span>}
    </div>
    <OnboardingCollapse open={open} id={contentId}><div className="border-t border-slate-100 py-2 pl-3">
      {rows.map((row) => <LocationDisclosure key={row.store_id} header={<>
        {row.progress.status === "completed" && <CheckCircleRoundedIcon className="text-emerald-500" fontSize="small" />}
        <span className="font-medium">{row.store_name}</span><span className="text-xs text-slate-500">{row.progress.completed} / {row.progress.total} steps</span>
        <span className="ml-auto text-xs text-slate-500">{labels[row.progress.status]}</span>
      </>}><div className="mb-3 ml-3 mr-10 border-l border-slate-200 pl-4">
        {row.progress.sourceRunId && <p className="mb-2 text-xs text-slate-500">Recorded pipeline · {row.progress.dateRange?.start} to {row.progress.dateRange?.end}<span className="block">Run {row.progress.sourceRunId}</span></p>}
        {row.progress.rows.map((item) => <div key={item.key} className="flex items-center justify-between gap-3 py-2 text-xs text-slate-600"><span>{item.label}</span><span>{item.total > 1 ? `${item.completed}/${item.total} · ` : ""}{labels[item.status] || (item.status === "not_applicable" ? "Complete" : "Waiting")}</span></div>)}
      </div></LocationDisclosure>)}
    </div></OnboardingCollapse>
  </section>;
}
function Journey({ run, stores = [], manualModelTask, onOpenLaborModel, onStart, disabled, blockedReason, revealToken, requestedPhase }) {
  return <div aria-label="Data preparation steps">{displayedJourney(run, stores).map((step) => step.id === "model" && modelProgress(manualModelTask) ? <ManualModelProgress key="model" task={manualModelTask} onOpen={onOpenLaborModel} /> : <PreparationPhase revealToken={revealToken} requestedPhase={requestedPhase} key={step.id} step={step} run={run} stores={stores} onStart={step.id === "model" && onOpenLaborModel ? onOpenLaborModel : onStart} disabled={step.id === "model" && onOpenLaborModel ? false : disabled} blockedReason={step.id === "model" && onOpenLaborModel ? "" : blockedReason} />)}</div>;
}

function InitialLoadingState() {
  return (
    <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 px-5 py-6" aria-live="polite">
      <div className="flex items-center gap-3 text-sm font-medium text-slate-700">
        <CircularProgress size={20} thickness={5} sx={{ color: "#6d28d9" }} />
        Checking for an existing setup run…
      </div>
      <div className="mt-5 h-2 animate-pulse rounded-full bg-slate-200" />
    </div>
  );
}

function EmptyState({ manualModelTask, onOpenLaborModel, disabled, starting, onStart, startBlockedReason, stores, revealToken, requestedPhase }) {
  return <Journey manualModelTask={manualModelTask} onOpenLaborModel={starting ? null : onOpenLaborModel} revealToken={revealToken} requestedPhase={requestedPhase} stores={stores} onStart={onStart} disabled={disabled || starting} blockedReason={startBlockedReason} />;
}

function LocationReview({
  run,
  decisions,
  storeSummaries,
  confirming,
  onDecisionChange,
  onConfirm,
}) {
  const preflightStores = Array.isArray(run?.preflight_results?.stores)
    ? run.preflight_results.stores
    : [];
  const preflightByStoreId = new Map(
    preflightStores.map((store) => [Number(store.store_id), store])
  );
  const summaryByStoreId = new Map(
    storeSummaries.map((store) => [Number(store.store_id), store])
  );
  const groupOptions = getGroupOptions(run, decisions);
  const validationMessage = getDecisionValidationMessage(decisions);
  const displayDecisions = [...decisions].sort((left, right) => {
    const leftFlagged = Boolean(preflightByStoreId.get(Number(left.store_id))?.reason_codes?.length);
    const rightFlagged = Boolean(preflightByStoreId.get(Number(right.store_id))?.reason_codes?.length);
    if (leftFlagged !== rightFlagged) return leftFlagged ? -1 : 1;
    const leftName = summaryByStoreId.get(Number(left.store_id))?.store_name || "";
    const rightName = summaryByStoreId.get(Number(right.store_id))?.store_name || "";
    return leftName.localeCompare(rightName);
  });
  const anomalyCount = displayDecisions.filter(
    (decision) => preflightByStoreId.get(Number(decision.store_id))?.reason_codes?.length
  ).length;

  return (
    <div className="mt-5 overflow-hidden rounded-2xl border border-amber-200 bg-amber-50/60">
      <div className="flex items-start gap-3 border-b border-amber-200 px-5 py-4">
        <WarningAmberRoundedIcon className="mt-0.5 text-amber-700" fontSize="small" />
        <div>
          <h3 className="text-sm font-semibold text-amber-950">
            Review {anomalyCount} location {anomalyCount === 1 ? "recommendation" : "recommendations"}
          </h3>
          <p className="mt-1 text-sm leading-6 text-amber-900/80">
            We preselected the safest defaults. Confirm which locations should train
            each productivity group, then the rest of setup will continue automatically.
          </p>
        </div>
      </div>

      <div className="divide-y divide-amber-100 bg-white">
        {displayDecisions.map((decision) => {
          const storeId = Number(decision.store_id);
          const preflightStore = preflightByStoreId.get(storeId) || {};
          const storeSummary = summaryByStoreId.get(storeId) || {};
          const reasonCodes = Array.isArray(preflightStore.reason_codes)
            ? preflightStore.reason_codes
            : [];
          const flagged = reasonCodes.length > 0;
          const observedSplh =
            preflightStore?.metrics?.weighted_splh ??
            preflightStore?.metrics?.median_splh;

          return (
            <div key={storeId} className={`px-5 py-4 ${flagged ? "bg-amber-50/30" : "bg-white"}`}>
              <div className="grid grid-cols-[minmax(0,1fr)_auto_220px] items-center gap-5 lg:grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-1">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <LocationOnRoundedIcon fontSize="small" className="text-slate-400" />
                    <p className="truncate text-sm font-semibold text-[#1C2541]">
                      {storeSummary.store_name || `Location ${storeId}`}
                    </p>
                    {flagged && (
                      <span className="rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
                        Check recommended
                      </span>
                    )}
                  </div>
                  {reasonCodes.length > 0 ? (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {reasonCodes.map((reasonCode) => (
                        <span
                          key={reasonCode}
                          className="rounded-md bg-amber-100/80 px-2 py-1 text-xs text-amber-900"
                        >
                          {formatReasonCode(reasonCode)}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-1 text-xs text-slate-500">No data anomaly detected.</p>
                  )}
                  {Number.isFinite(observedSplh) && (
                    <p className="mt-2 text-xs text-slate-500">
                      Observed productivity: {Number(observedSplh).toFixed(1)} sales per labor hour
                    </p>
                  )}
                </div>

                <label className="flex min-h-[44px] cursor-pointer items-center rounded-xl px-1 text-sm font-medium text-slate-700 focus-within:ring-2 focus-within:ring-violet-500 focus-within:ring-offset-2">
                  <Checkbox
                    checked={!decision.excluded_from_fit}
                    onChange={(event) =>
                      onDecisionChange(storeId, {
                        excluded_from_fit: !event.target.checked,
                      })
                    }
                    disabled={confirming}
                    inputProps={{
                      "aria-label": `Include ${storeSummary.store_name || `location ${storeId}`} in optimization fit`,
                    }}
                    sx={{
                      color: "#94a3b8",
                      "&.Mui-checked": { color: "#6d28d9" },
                    }}
                  />
                  Include in fit
                </label>

                <div className="lg:col-span-2 lg:w-full sm:col-span-1">
                  <label className="sr-only" id={`productivity-${storeId}-label`} htmlFor={`productivity-${storeId}`}>Productivity group for {storeSummary.store_name || `location ${storeId}`}</label>
                  <RightworkSelect id={`productivity-${storeId}`} value={decision.assigned_group_key || ""}
                    onChange={(event) => onDecisionChange(storeId, { assigned_group_key: event.target.value })}
                    disabled={confirming} options={[{ value: "", label: "Choose a productivity group", disabled: true }, ...groupOptions.map((group) => ({ value: group.key, label: group.name }))]} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between gap-4 border-t border-amber-200 bg-amber-50 px-5 py-4 sm:flex-col sm:items-stretch">
        <div className="min-w-0">
          {validationMessage ? (
            <p className="text-sm font-medium text-rose-700" role="alert">
              {validationMessage}
            </p>
          ) : (
            <p className="text-xs leading-5 text-amber-900/80">
              Included locations train their group. Excluded locations still receive the
              published standards for their assigned group.
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onConfirm}
          disabled={confirming || Boolean(validationMessage) || !decisions.length}
          className={`${PRIMARY_BUTTON_CLASSES} shrink-0 sm:w-full`}
        >
          {confirming && <ActionSpinner />}
          {confirming ? "Confirming…" : "Confirm and continue"}
        </button>
      </div>
    </div>
  );
}

export default function OnboardingRecommendedSetupCard({
  companyId,
  storeSummaries = [],
  refreshSummary,
  refreshToken = null,
  onActiveChange,
  startBlockedReason = "",
  revealToken = null,
  requestedPhase = null,
  internalOnly = false,
  additionalTasks = [],
  renderAdditionalTask,
  onOpenLaborModel = null,
  manualModelTask = null,
}) {
  const [open, setOpen] = React.useState(true);
  React.useEffect(() => { if (revealToken) setOpen(true); }, [revealToken]);
  const contentId = React.useId();
  const [run, setRun] = React.useState(null);
  const [initialLoading, setInitialLoading] = React.useState(true);
  const [latestRunResolved, setLatestRunResolved] = React.useState(false);
  const [action, setAction] = React.useState("");
  const [errorMessage, setErrorMessage] = React.useState("");
  const [noticeMessage, setNoticeMessage] = React.useState("");
  const [locationDecisions, setLocationDecisions] = React.useState([]);

  const mountedRef = React.useRef(false);
  const requestSequenceRef = React.useRef(0);
  const pollInFlightRef = React.useRef(false);
  const actionSequenceRef = React.useRef(0);
  const clientRequestIdRef = React.useRef(null);
  const reviewKeyRef = React.useRef("");
  const refreshedCompletedRunRef = React.useRef("");
  const lastRefreshTokenRef = React.useRef(refreshToken);

  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestSequenceRef.current += 1;
    };
  }, []);

  const loadLatest = React.useCallback(
    async ({ background = false } = {}) => {
      if (!companyId) {
        if (mountedRef.current) {
          setRun(null);
          setLatestRunResolved(true);
          setInitialLoading(false);
        }
        return null;
      }

      const requestSequence = ++requestSequenceRef.current;
      if (!background && mountedRef.current) setInitialLoading(true);

      try {
        const response = await getRightworkAPI(API_get_onboarding_setup_run, {
          company_id: companyId,
        });
        const latestRun = extractOnboardingRun(response);
        if (!mountedRef.current || requestSequence !== requestSequenceRef.current) {
          return latestRun;
        }
        setRun(latestRun);
        setLatestRunResolved(true);
        setErrorMessage("");
        return latestRun;
      } catch (error) {
        if (!mountedRef.current || requestSequence !== requestSequenceRef.current) {
          return null;
        }
        if (error?.response?.status === 404) {
          setRun(null);
          setLatestRunResolved(true);
          setErrorMessage("");
          setNoticeMessage("");
          return null;
        }

        // Keep the last successful run visible if a background refresh is transiently unavailable.
        setErrorMessage(
          getOnboardingErrorMessage(
            error,
            background
              ? "Progress could not be refreshed. We’ll try again automatically."
              : "Unable to load recommended setup. Please try again."
          )
        );
        return null;
      } finally {
        if (mountedRef.current && requestSequence === requestSequenceRef.current) {
          setInitialLoading(false);
        }
      }
    },
    [companyId]
  );

  React.useEffect(() => {
    requestSequenceRef.current += 1;
    actionSequenceRef.current += 1;
    reviewKeyRef.current = "";
    clientRequestIdRef.current = null;
    setRun(null);
    setLatestRunResolved(false);
    setLocationDecisions([]);
    setAction("");
    setErrorMessage("");
    setNoticeMessage("");
    setInitialLoading(Boolean(companyId));
    loadLatest();
  }, [companyId, loadLatest]);

  // An external refresh token (e.g. a setup action confirmed through the
  // assistant) refetches the latest run. Skip the initial mount and any
  // refetch while an action is in flight.
  const actionRef = React.useRef(action);
  actionRef.current = action;
  React.useEffect(() => {
    if (refreshToken === lastRefreshTokenRef.current) return;
    lastRefreshTokenRef.current = refreshToken;
    if (refreshToken == null || actionRef.current) return;
    loadLatest({ background: true });
  }, [loadLatest, refreshToken]);

  React.useEffect(() => {
    if (
      action ||
      !run ||
      needsDispatchRetry(run) ||
      !(shouldPollOnboardingRun(run) || (run.status === "completed" && run.result_summary?.requested_phase && run.result_summary.requested_phase !== run.preparation_phase))
    ) {
      return undefined;
    }

    const intervalId = window.setInterval(async () => {
      if (pollInFlightRef.current || !mountedRef.current) return;
      pollInFlightRef.current = true;
      try {
        await loadLatest({ background: true });
      } finally {
        pollInFlightRef.current = false;
      }
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [action, loadLatest, run]);

  React.useEffect(() => {
    if (run?.status !== REVIEW_STATUS) {
      reviewKeyRef.current = "";
      return;
    }
    const reviewKey = `${run.uuid}:${run.preflight_decision_version}`;
    if (reviewKeyRef.current === reviewKey) return;
    reviewKeyRef.current = reviewKey;
    setLocationDecisions(buildDefaultLocationDecisions(run));
  }, [run]);

  React.useEffect(() => {
    if (
      run?.status !== "completed" ||
      !run.uuid ||
      refreshedCompletedRunRef.current === run.uuid ||
      typeof refreshSummary !== "function"
    ) {
      return;
    }
    let cancelled = false;
    let retryTimer = null;
    const refreshCompletedSummary = async (attempt = 1) => {
      try {
        await refreshSummary();
        if (!cancelled) {
          refreshedCompletedRunRef.current = run.uuid;
        }
      } catch (error) {
        if (cancelled) return;
        if (attempt < 3) {
          retryTimer = window.setTimeout(
            () => refreshCompletedSummary(attempt + 1),
            COMPLETION_REFRESH_RETRY_MS
          );
          return;
        }
        console.error("Unable to refresh onboarding summary after setup", error);
      }
    };
    refreshCompletedSummary();
    return () => {
      cancelled = true;
      if (retryTimer) window.clearTimeout(retryTimer);
    };
  }, [refreshSummary, run]);

  React.useEffect(() => {
    if (typeof onActiveChange === "function") {
      const runActive = isOnboardingRunActive(run);
      const ready = latestRunResolved && !initialLoading;
      onActiveChange(!ready || Boolean(action) || runActive, {
        ready,
        runActive,
      });
    }
  }, [action, initialLoading, latestRunResolved, onActiveChange, run]);

  const recoverSavedPlan = React.useCallback(async () => {
    setNoticeMessage("Your setup plan was saved. Reconnecting to the run now…");
    await loadLatest({ background: true });
  }, [loadLatest]);

  const handleStart = async (phase = "outputs") => {
    if (!companyId || action || startBlockedReason) return;
    const requestSequence = ++requestSequenceRef.current;
    const actionSequence = ++actionSequenceRef.current;
    setAction("starting");
    setErrorMessage("");
    setNoticeMessage("");
    if (!clientRequestIdRef.current) {
      clientRequestIdRef.current = createClientRequestId();
    }

    try {
      const response = await postRightworkAPI(API_start_onboarding_setup, {
        company_id: companyId,
        client_request_id: clientRequestIdRef.current,
        preset_version: "recommended_v1",
        preparation_phase: phase,
      });
      if (!mountedRef.current || requestSequence !== requestSequenceRef.current) return;
      const startedRun = extractOnboardingRun(response);
      setRun(startedRun);
      clientRequestIdRef.current = null;
      setNoticeMessage("");
    } catch (error) {
      if (!mountedRef.current || requestSequence !== requestSequenceRef.current) return;
      if (isSavedPlanDispatchError(error)) {
        await recoverSavedPlan();
      } else if (error?.response?.status === 409) {
        setNoticeMessage("An existing setup run is already active. Loading it now…");
        await loadLatest({ background: true });
      } else {
        setErrorMessage(
          getOnboardingErrorMessage(error, "Unable to start recommended setup.")
        );
      }
    } finally {
      if (mountedRef.current && actionSequence === actionSequenceRef.current) {
        setAction("");
      }
    }
  };

  const handleDecisionChange = (storeId, updates) => {
    setLocationDecisions((current) =>
      current.map((decision) =>
        Number(decision.store_id) === Number(storeId)
          ? { ...decision, ...updates }
          : decision
      )
    );
  };

  const handleConfirm = async () => {
    if (!companyId || !run?.uuid || action || getDecisionValidationMessage(locationDecisions)) {
      return;
    }
    const requestSequence = ++requestSequenceRef.current;
    const actionSequence = ++actionSequenceRef.current;
    setAction("confirming");
    setErrorMessage("");
    setNoticeMessage("");

    try {
      const response = await postRightworkAPI(
        API_confirm_onboarding_setup_locations,
        {
          company_id: companyId,
          run_uuid: run.uuid,
          review_version: run.preflight_decision_version,
          location_decisions: locationDecisions,
        }
      );
      if (!mountedRef.current || requestSequence !== requestSequenceRef.current) return;
      setRun(extractOnboardingRun(response));
      setNoticeMessage("Location recommendations confirmed. Setup is continuing.");
    } catch (error) {
      if (!mountedRef.current || requestSequence !== requestSequenceRef.current) return;
      if (isSavedPlanDispatchError(error)) {
        await recoverSavedPlan();
      } else if (error?.response?.status === 409) {
        setNoticeMessage("This review changed in another session. Loading the latest decisions…");
        await loadLatest({ background: true });
      } else {
        setErrorMessage(
          getOnboardingErrorMessage(
            error,
            "Unable to confirm location recommendations."
          )
        );
      }
    } finally {
      if (mountedRef.current && actionSequence === actionSequenceRef.current) {
        setAction("");
      }
    }
  };

  const handleRetry = async () => {
    if (!companyId || !run?.uuid || action) return;
    const requestSequence = ++requestSequenceRef.current;
    const actionSequence = ++actionSequenceRef.current;
    setAction("retrying");
    setErrorMessage("");
    setNoticeMessage("");

    try {
      const response = await postRightworkAPI(API_retry_onboarding_setup, {
        company_id: companyId,
        run_uuid: run.uuid,
      });
      if (!mountedRef.current || requestSequence !== requestSequenceRef.current) return;
      setRun(extractOnboardingRun(response));
      setNoticeMessage("Retry started from the last saved checkpoint.");
    } catch (error) {
      if (!mountedRef.current || requestSequence !== requestSequenceRef.current) return;
      if (isSavedPlanDispatchError(error)) {
        await recoverSavedPlan();
      } else if (error?.response?.status === 409) {
        setNoticeMessage("The run changed before the retry started. Loading its latest checkpoint…");
        await loadLatest({ background: true });
      } else {
        setErrorMessage(
          getOnboardingErrorMessage(error, "Unable to retry recommended setup.")
        );
      }
    } finally {
      if (mountedRef.current && actionSequence === actionSequenceRef.current) {
        setAction("");
      }
    }
  };

  const dispatchStatus = run?.result_summary?.dispatch_status;
  const dispatchNeedsRetry = needsDispatchRetry(run);
  const showRetry = run?.status === "failed" || dispatchNeedsRetry;
  const runActive = isOnboardingRunActive(run);
  const runTerminal = isOnboardingRunTerminal(run);
  const retryPresentation = getRetryPresentation(run, dispatchStatus);
  const RetryIcon = retryPresentation.Icon;

  return (
    <section
      className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
      aria-labelledby="recommended-onboarding-title"
    >
      <CardHeader manualModelTask={manualModelTask} stores={storeSummaries} internalOnly={internalOnly} additionalTasks={additionalTasks} run={run} open={open} onToggle={() => setOpen(!open)} contentId={contentId} />
      <OnboardingCollapse open={open} id={contentId}>
      <div className="px-5 pb-4">
      {initialLoading && !run ? (
        <InitialLoadingState />
      ) : !run ? (
        <EmptyState manualModelTask={manualModelTask} onOpenLaborModel={onOpenLaborModel} revealToken={revealToken} requestedPhase={requestedPhase}
          disabled={!companyId || Boolean(startBlockedReason)}
          startBlockedReason={startBlockedReason}
          stores={storeSummaries}
          starting={action === "starting"}
          onStart={handleStart}
        />
      ) : (
        <>
          {(runActive || runTerminal) && !dispatchNeedsRetry && (
            <Journey manualModelTask={manualModelTask} onOpenLaborModel={onOpenLaborModel} revealToken={revealToken} requestedPhase={requestedPhase} run={run} stores={storeSummaries} onStart={handleStart} disabled={Boolean(action) || Boolean(startBlockedReason)} blockedReason={startBlockedReason} />
          )}
          {run.status === REVIEW_STATUS && (
            <LocationReview
              run={run}
              decisions={locationDecisions}
              storeSummaries={storeSummaries}
              confirming={action === "confirming"}
              onDecisionChange={handleDecisionChange}
              onConfirm={handleConfirm}
            />
          )}
          {showRetry && (
            <div className={`mt-5 flex items-start justify-between gap-5 rounded-2xl border p-5 sm:flex-col ${retryPresentation.containerClasses}`}>
              <div className="flex min-w-0 items-start gap-3">
                <RetryIcon className={`mt-0.5 shrink-0 ${retryPresentation.textClasses}`} fontSize="small" />
                <div>
                  <p className={`text-sm font-semibold ${retryPresentation.textClasses}`}>
                    {retryPresentation.heading}
                  </p>
                  <p className={`mt-1 text-sm leading-6 ${retryPresentation.textClasses}`}>
                    {retryPresentation.description}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleRetry}
                disabled={Boolean(action)}
                className={`${SECONDARY_BUTTON_CLASSES} shrink-0 sm:w-full ${retryPresentation.buttonClasses}`}
              >
                {action === "retrying" ? <ActionSpinner /> : <RefreshRoundedIcon fontSize="small" />}
                {action === "retrying" ? "Retrying…" : "Retry setup"}
              </button>
            </div>
          )}
        </>
      )}

      {additionalTasks.length > 0 && <div className="border-t border-slate-100">
        {additionalTasks.map((task) => renderAdditionalTask?.(task))}
      </div>}

      {noticeMessage && (
        <div className="mt-4 flex items-start gap-2 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800" role="status">
          <CheckCircleRoundedIcon fontSize="small" className="mt-0.5 shrink-0" />
          <span>{noticeMessage}</span>
        </div>
      )}
      {errorMessage && (
        <div className="mt-4 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800" role="alert">
          <ErrorOutlineRoundedIcon fontSize="small" className="mt-0.5 shrink-0" />
          <span className="min-w-0 flex-1">{errorMessage}</span>
          {!showRetry && (
            <button
              type="button"
              onClick={() => loadLatest({ background: true })}
              className="shrink-0 rounded-lg px-2 py-1 text-xs font-semibold text-rose-800 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500"
            >
              Refresh
            </button>
          )}
        </div>
      )}
      </div>
      </OnboardingCollapse>
    </section>
  );
}
